import { useQuery } from '@tanstack/react-query';
import { Bell, BellOff } from 'lucide-react';
import { useNavigate } from 'react-router';
import { api } from '@/lib/api/client';
import { relativeTime } from '@/lib/format';
import { rowsOf } from '@/lib/search/globalSearch';
import { useI18n } from '@/lib/i18n';
import { IconButton } from '@/components/ui/Button';
import { Popover } from '@/components/ui/Popover';

interface NotificationItem {
  id: number;
  title: string;
  body: string;
  createdAt: string;
  href?: string;
  read: boolean;
}

function toItems(payload: unknown): NotificationItem[] {
  return rowsOf(payload)
    .filter((r) => typeof r.id === 'number')
    .map((r) => ({
      id: r.id as number,
      title: String(r.title ?? r.subject ?? 'Notification'),
      body: String(r.body ?? r.message ?? ''),
      createdAt: String(r.createdAt ?? ''),
      href: typeof r.href === 'string' ? r.href : undefined,
      read: Boolean(r.readAt ?? r.read),
    }));
}

/** Staff alerts (holds ready, jobs failed, imports finished). Quietly empty if the endpoint is unavailable. */
export function NotificationsBell() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { data } = useQuery({
    queryKey: ['notifications', 'staff'],
    retry: false,
    staleTime: 60_000,
    refetchInterval: 120_000,
    queryFn: ({ signal }) => api.get<unknown>('/notifications/me', { query: { pageSize: 8 }, signal }).then(toItems),
  });
  const items = data ?? [];
  const unread = items.filter((i) => !i.read).length;
  return (
    <Popover
      label={t('shell.notifications')}
      placement="bottom-end"
      className="w-[22rem] p-0"
      trigger={
        <IconButton
          label={unread ? `${t('shell.notifications')} (${unread})` : t('shell.notifications')}
          icon={
            <span className="relative inline-flex">
              <Bell className="h-[1.125rem] w-[1.125rem]" aria-hidden="true" />
              {unread > 0 && <span className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-sapphire-500 ring-2 ring-surface" aria-hidden="true" />}
            </span>
          }
        />
      }
    >
      {(close) => (
        <div>
          <div className="border-b border-line px-4 py-3">
            <p className="font-display text-sm font-semibold text-ink">{t('shell.notifications')}</p>
          </div>
          {items.length === 0 ? (
            <div className="flex flex-col items-center gap-2 px-6 py-10 text-center">
              <BellOff className="h-6 w-6 text-ink-subtle" aria-hidden="true" />
              <p className="text-sm text-ink-muted">{t('shell.noNotifications')}</p>
            </div>
          ) : (
            <ul className="max-h-80 divide-y divide-line overflow-auto scroll-thin">
              {items.map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    className="flex w-full gap-3 px-4 py-3 text-left transition hover:bg-sunken"
                    onClick={() => {
                      close();
                      if (item.href) {
                        navigate(item.href);
                      }
                    }}
                  >
                    <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${item.read ? 'bg-transparent' : 'bg-sapphire-500'}`} aria-hidden="true" />
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-medium text-ink">{item.title}</span>
                      <span className="line-clamp-2 text-xs text-ink-muted">{item.body}</span>
                      <span className="mt-0.5 block text-2xs text-ink-subtle">{relativeTime(item.createdAt)}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Popover>
  );
}
